/**
 * Display-only currency converter (ZAR -> USD).
 *
 * Converts on-page ZAR price strings (e.g. "R 1,234.56") into USD using a live
 * exchange rate. This does NOT change the checkout currency — the store settles
 * in ZAR. It rewrites displayed text only.
 *
 * Strategy:
 *  - Walk text nodes, match the shop's ZAR money format, and remember the
 *    original ZAR text on the owning element (data-cc-zar). Conversion is always
 *    derived from that stored original, so it is idempotent and reversible.
 *  - A MutationObserver re-processes prices that JS injects later (variant
 *    switching on the product page, cart drawer re-renders, etc.).
 *  - The chosen currency is persisted in localStorage.
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'cc-currency';
  var RATE_KEY = 'cc-zar-usd-rate';
  var RATE_TS_KEY = 'cc-zar-usd-rate-ts';
  var RATE_TTL_MS = 12 * 60 * 60 * 1000; // refresh at most every 12h
  var FALLBACK_RATE = 0.053; // ~ZAR->USD; only used if the live fetch fails

  // Matches "R 1,234.56", "R1 234,56", "R 99.00" etc. We normalise digits below.
  var ZAR_RE = /R\s?\d[\d.,\s ]*\d|R\s?\d/;

  var state = {
    currency: 'ZAR',
    rate: parseFloat(localStorage.getItem(RATE_KEY)) || FALLBACK_RATE,
    observer: null,
  };

  /* ------------------------------------------------------------------ */
  /* Rate fetching                                                       */
  /* ------------------------------------------------------------------ */

  function rateIsFresh() {
    var ts = parseFloat(localStorage.getItem(RATE_TS_KEY));
    return ts && Date.now() - ts < RATE_TTL_MS && localStorage.getItem(RATE_KEY);
  }

  function fetchRate() {
    if (rateIsFresh()) {
      state.rate = parseFloat(localStorage.getItem(RATE_KEY)) || state.rate;
      return Promise.resolve(state.rate);
    }
    // frankfurter.app: free, no API key, CORS-enabled.
    return fetch('https://api.frankfurter.app/latest?from=ZAR&to=USD')
      .then(function (r) {
        if (!r.ok) throw new Error('rate fetch failed');
        return r.json();
      })
      .then(function (data) {
        var rate = data && data.rates && data.rates.USD;
        if (rate && isFinite(rate)) {
          state.rate = rate;
          localStorage.setItem(RATE_KEY, String(rate));
          localStorage.setItem(RATE_TS_KEY, String(Date.now()));
        }
        return state.rate;
      })
      .catch(function () {
        // Keep whatever we had (cached or fallback).
        return state.rate;
      });
  }

  /* ------------------------------------------------------------------ */
  /* Parsing & formatting                                                */
  /* ------------------------------------------------------------------ */

  // Turn a ZAR display string into a Number of rands.
  function parseZar(text) {
    var cleaned = text.replace(/[Rr]/, '').replace(/[\s ]/g, '').trim();
    if (!cleaned) return null;
    // Determine decimal separator: the last '.' or ',' followed by exactly 2 digits.
    var decMatch = cleaned.match(/[.,](\d{2})$/);
    var decimals = '00';
    var integerPart = cleaned;
    if (decMatch) {
      decimals = decMatch[1];
      integerPart = cleaned.slice(0, cleaned.length - 3);
    }
    integerPart = integerPart.replace(/[.,\s]/g, '');
    if (!/^\d+$/.test(integerPart)) return null;
    return parseFloat(integerPart + '.' + decimals);
  }

  function formatUsd(amount) {
    return (
      '$' +
      amount.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    );
  }

  /* ------------------------------------------------------------------ */
  /* DOM walking                                                         */
  /* ------------------------------------------------------------------ */

  // Elements whose direct text contains a price get annotated on their closest
  // sensible wrapper. We annotate the text node's parent element.
  function annotate(el, originalText) {
    if (el.getAttribute('data-cc-zar') === null) {
      el.setAttribute('data-cc-zar', originalText);
    }
  }

  // Walk a root and collect every *unconverted* ZAR text node. Newly rendered
  // prices are always ZAR, so pattern-matching on "R…" is the right way to
  // DISCOVER nodes. Re-displaying a tracked node (e.g. USD -> ZAR) does not rely
  // on this — that walks the tracked set instead, whose nodes may show USD text
  // and therefore would not match the ZAR pattern here.
  function collectNewPriceNodes(root) {
    var found = [];
    var walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
      acceptNode: function (node) {
        if (!node.nodeValue || node.nodeValue.indexOf('R') === -1) {
          return NodeFilter.FILTER_REJECT;
        }
        var parent = node.parentNode;
        if (!parent) return NodeFilter.FILTER_REJECT;
        // Skip our own widget and script/style.
        var tag = parent.nodeName;
        if (tag === 'SCRIPT' || tag === 'STYLE' || tag === 'TEXTAREA') {
          return NodeFilter.FILTER_REJECT;
        }
        if (parent.closest && parent.closest('currency-converter')) {
          return NodeFilter.FILTER_REJECT;
        }
        return ZAR_RE.test(node.nodeValue)
          ? NodeFilter.FILTER_ACCEPT
          : NodeFilter.FILTER_REJECT;
      },
    });
    var n;
    while ((n = walker.nextNode())) {
      found.push(n);
    }
    return found;
  }

  // Tracked price text nodes. We keep an explicit, iterable Set (not just a
  // WeakMap) so we can re-apply to nodes whose current text is USD — those no
  // longer match the ZAR pattern and so can't be re-discovered by a DOM scan.
  var tracked = new Set();
  // node -> original ZAR string.
  var originals = new WeakMap();

  function register(node) {
    if (!originals.has(node)) {
      originals.set(node, node.nodeValue);
      if (node.parentElement) annotate(node.parentElement, node.nodeValue.trim());
    }
    tracked.add(node);
  }

  function applyToNode(node) {
    // Drop nodes that have left the document so the set can't grow unbounded.
    if (!node.isConnected) {
      tracked.delete(node);
      originals.delete(node);
      return;
    }

    var original = originals.get(node);
    if (original === undefined) {
      original = node.nodeValue;
      originals.set(node, original);
      if (node.parentElement) annotate(node.parentElement, original.trim());
    }

    if (state.currency === 'ZAR') {
      if (node.nodeValue !== original) node.nodeValue = original;
      return;
    }

    // USD: replace each ZAR token in the string with its USD equivalent.
    var replaced = original.replace(
      /R\s?\d[\d.,\s ]*\d|R\s?\d/g,
      function (match) {
        var rands = parseZar(match);
        if (rands === null) return match;
        return formatUsd(rands * state.rate);
      }
    );
    if (node.nodeValue !== replaced) node.nodeValue = replaced;
  }

  // Discover any new ZAR nodes under `root`, register them, and apply current mode.
  function processRoot(root) {
    var nodes = collectNewPriceNodes(root);
    for (var i = 0; i < nodes.length; i++) {
      register(nodes[i]);
      applyToNode(nodes[i]);
    }
  }

  // Re-apply the current mode to every node we already track. This is what makes
  // USD -> ZAR work: those nodes show USD text and would be invisible to a scan.
  function reapplyTracked() {
    tracked.forEach(function (node) {
      applyToNode(node);
    });
  }

  function applyAll() {
    // First refresh already-known nodes (covers USD -> ZAR restoration), then
    // discover/convert any that appeared since the last pass.
    reapplyTracked();
    processRoot(document.body);
  }

  /* ------------------------------------------------------------------ */
  /* Observer for dynamically injected/updated prices                    */
  /* ------------------------------------------------------------------ */

  function startObserver() {
    if (state.observer) return;
    state.observer = new MutationObserver(function (mutations) {
      // Cheap guard: only react when something looks price-ish changed.
      var touched = [];
      for (var i = 0; i < mutations.length; i++) {
        var m = mutations[i];
        if (m.type === 'characterData') {
          var tn = m.target;
          if (tn && tn.nodeValue && tn.nodeValue.indexOf('R') !== -1) {
            // The app wrote a fresh ZAR string into this node (e.g. a variant
            // switch or cart re-render). That value is authoritative, so drop
            // the stale baseline and re-read it. This runs while our own writes
            // are paused (we disconnect during applyAll/rAF), so we only ever
            // see genuine app updates here — never our own USD output.
            originals.delete(tn);
            touched.push(tn.parentNode || tn);
          }
        } else if (m.addedNodes && m.addedNodes.length) {
          for (var j = 0; j < m.addedNodes.length; j++) {
            var an = m.addedNodes[j];
            if (an.nodeType === 1) touched.push(an);
            else if (an.nodeType === 3 && an.parentNode) touched.push(an.parentNode);
          }
        }
      }
      if (!touched.length) return;
      // Debounce to the next frame to avoid thrashing during big re-renders.
      if (state._raf) return;
      state._pending = (state._pending || []).concat(touched);
      state._raf = requestAnimationFrame(function () {
        state._raf = null;
        var pending = state._pending || [];
        state._pending = null;
        // Temporarily disconnect so our own text writes don't re-trigger us.
        state.observer.disconnect();
        for (var k = 0; k < pending.length; k++) {
          var el = pending[k];
          if (el && el.nodeType === 1 && document.contains(el)) {
            processRoot(el);
          }
        }
        connectObserver();
      });
    });
    connectObserver();
  }

  function connectObserver() {
    if (!state.observer) return;
    state.observer.observe(document.body, {
      childList: true,
      characterData: true,
      subtree: true,
    });
  }

  /* ------------------------------------------------------------------ */
  /* UI wiring                                                           */
  /* ------------------------------------------------------------------ */

  function setCurrency(currency, widget) {
    state.currency = currency === 'USD' ? 'USD' : 'ZAR';
    localStorage.setItem(STORAGE_KEY, state.currency);

    // Disconnect while we rewrite, then reconnect.
    if (state.observer) state.observer.disconnect();
    applyAll();
    connectObserver();

    // Reflect in the widget.
    if (widget) {
      var codeEl = widget.querySelector('[data-cc-active-code]');
      if (codeEl) codeEl.textContent = state.currency;
      widget.querySelectorAll('[data-cc-set]').forEach(function (btn) {
        btn.classList.toggle('is-active', btn.getAttribute('data-cc-set') === state.currency);
      });
      var note = widget.querySelector('[data-cc-note]');
      if (note) note.hidden = state.currency !== 'USD';
    }
  }

  function init(widget) {
    var tab = widget.querySelector('.cc__tab');
    var panel = widget.querySelector('.cc__panel');

    function openPanel() {
      panel.hidden = false;
      tab.setAttribute('aria-expanded', 'true');
      widget.classList.add('is-open');
    }
    function closePanel() {
      panel.hidden = true;
      tab.setAttribute('aria-expanded', 'false');
      widget.classList.remove('is-open');
    }

    tab.addEventListener('click', function () {
      if (panel.hidden) openPanel();
      else closePanel();
    });

    widget.querySelectorAll('[data-cc-set]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        setCurrency(btn.getAttribute('data-cc-set'), widget);
        closePanel();
      });
    });

    // Close when clicking outside.
    document.addEventListener('click', function (e) {
      if (!widget.contains(e.target) && !panel.hidden) closePanel();
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && !panel.hidden) closePanel();
    });

    // Restore persisted choice, then fetch a fresh rate and re-apply.
    var saved = localStorage.getItem(STORAGE_KEY) === 'USD' ? 'USD' : 'ZAR';

    fetchRate().then(function () {
      startObserver();
      setCurrency(saved, widget);
    });
  }

  function boot() {
    var widget = document.querySelector('currency-converter');
    if (widget) init(widget);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
